import data from "@emoji-mart/data";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalTypeaheadMenuPlugin } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { SearchIndex, init } from "emoji-mart";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { $createEmojiNode } from "../../nodes/emoji-node";
import { EmojiTypeaheadOption } from "../../types/emoji-typeahead-option";
import { useBasicTypeaheadTriggerMatch } from "../../utils/use-basic-typeahead-trigger-match";
import { TypeaheadOptionsList } from "./typeahead-option-list";

import type { Emoji } from "../../types/emoji";
import type { TextNode } from "lexical";
import type { FC } from "react";

const useEmojiLookupService = (searchText: string | null): Emoji[] => {
  const [results, setResults] = useState<Emoji[]>([]);

  useEffect(() => {
    const initFn = async (): Promise<void> => {
      await init({ data });
    };

    initFn().catch(() => console.error("Failed to initialise emoji data."));
  }, []);

  useEffect(() => {
    const searchFn = async (): Promise<void> => {
      if (searchText === null) {
        setResults([]);
        return;
      }

      const matches = (await SearchIndex.search(searchText, {
        maxResults: 10,
        caller: null,
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      })) as { id: string; skins?: { native?: string }[] }[];

      const nativeEmojis = matches.reduce<Emoji[]>((arr, val) => {
        if (val.skins?.some((x) => x.native)) {
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          const native: string = val.skins.find((x) => x.native)?.native as string;
          arr.push({ id: val.id, native });
        }

        return arr;
      }, []);

      setResults(nativeEmojis);
    };
    searchFn().catch(console.error);
  }, [searchText]);

  return results;
};

export const EmojiTypeaheadPlugin: FC = () => {
  const [queryString, setQueryString] = useState<string | null>(null);
  const results = useEmojiLookupService(queryString);
  const [editor] = useLexicalComposerContext();

  const onSelectOption = useCallback(
    (selected: EmojiTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const emojiNode = $createEmojiNode(selected.emoji.id, selected.emoji.native);
        if (nodeToReplace) {
          nodeToReplace.replace(emojiNode);
        }
        emojiNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const options = useMemo(
    () =>
      results
        .map(
          (result) =>
            new EmojiTypeaheadOption(
              result,
              <>
                <span>{result.native}</span>
                <span>{` :${result.id}:`}</span>
              </>,
            ),
        )
        .slice(0, 10),
    [results],
  );

  const PUNCTUATION = String.raw`\.,\*\?\$\@\|#{}\(\)\^\[\]\\/!%'"~=<>_:;`;
  const triggerFn = useBasicTypeaheadTriggerMatch(":", {
    minLength: 2,
    ignorePunctuation: PUNCTUATION,
  });

  return (
    <LexicalTypeaheadMenuPlugin<EmojiTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={options}
      triggerFn={triggerFn}
      menuRenderFn={(anchorElementRef, renderProps) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <TypeaheadOptionsList
                data-testid="emoji-typeahead"
                {...renderProps}
                options={options}
                renderOption={(option) => option.display}
              />,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
};
